<template>
  <div class="tw-font-body">
    <PreJamxDownloadsPopup
      v-if="preJamxShows.length"
      :visible="preJamxModalVisible"
      :shows="preJamxShows"
      :saving="savingPreJamx"
      :loading="loadingPreJamx"
      @save="handlePreJamxSave"
      @remind-later="handlePreJamxRemindLater"
      @dismiss="handlePreJamxDismiss"
    />
    <div
      class="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-center sm:tw-px-2 tw-gap-3 md:tw-gap-0 tw-pt-2 tw-pb-5 tw-flex-wrap"
    >
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 tw-w-full sm:tw-w-auto"
      >
        <ShowsDropdown @select-show="selectShow" />
        <EpisodesDropdown
          :class="[selectedShow === 'all' ? 'tw-hidden' : '']"
          :selected-show="selectedShow"
          @select-episode="selectEpisode"
        />
      </div>
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 tw-w-full sm:tw-w-auto tw-justify-end"
      >
        <Dropdown
          v-if="connectedPublisherId"
          v-model="campaignType"
          :options="campaignTypeOptions"
          optionValue="value"
          optionLabel="label"
          placeholder="All Campaigns"
          class="tw-border tw-border-transparent tw-outline-none tw-shadow dark:tw-bg-slate-800 dark:tw-border-slate-700"
          input-class="tw-p-1 tw-pl-2 tw-text-sm tw-font-medium"
        >
          <template #value="slotProps">
            <div class="tw-flex tw-gap-2 tw-items-center">
              <i class="pi pi-megaphone tw-text-sm"></i>
              <span class="tw-capitalize">
                {{ slotProps.value ? slotProps.value : slotProps.placeholder }}
              </span>
            </div>
          </template>
        </Dropdown>
        <DateMenu @set-date-range="setDateRange" />
      </div>
    </div>
    <div class="tw-flex tw-gap-2 tw-flex-wrap">
      <OverviewCard
        v-if="showOverviewPlaceholder && !loadingOverviews"
        icon="pi-eye"
        title="Overview"
        :placeholder="true"
        placeholder-message="Your metrics will be here..."
        :active-card="true"
        :clickable="false"
      />
      <template v-else>
        <OverviewCard
          v-for="overview in overviews"
          :key="overview.key"
          :icon="overview.icon"
          :title="overview.title"
          :value="overview.total"
          :second-value="overview.diff"
          :loading="loadingOverviews"
          :active-card="overview.type === overtimeEventType"
          :clickable="overview.clickable"
          :tooltip="overview.tooltip"
          :class="[loadingOverviews && 'tw-pointer-events-none']"
          @click="
            handleActiveOverviewCard(
              overview.type,
              overview.status,
              overview.clickable
            )
          "
        />
      </template>
    </div>
    <div class="tw-mt-4">
      <OvertimeCard
        :data="overtimeData"
        :loading="loadingOvertimeData"
        :title="overtimeType"
      />
    </div>
    <div
      class="tw-mt-4 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-items-stretch"
    >
      <TopCountriesCard
        :data="topByCountries"
        :loading="loadingTopByCountries"
        :type="type"
      />
      <ByDeviceCard :data="topByDevices" :loading="loadingTopByDevices" />
    </div>
    <div v-if="selectedEpisode === 'all'" class="tw-mt-4">
      <ShowsTable
        :data="showsData"
        :loading="loadingShowsData"
        :selected-show="selectedShow"
        @refresh-data="fetchShowsData"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";
import Dropdown from "primevue/dropdown";
import ShowsDropdown from "./components/ShowsDropdown.vue";
import EpisodesDropdown from "./components/EpisodesDropdown.vue";
import { useStore } from "vuex";
import moment from "moment";
import axios from "axios";
import { API_BASE_URL } from "@/consts";
import OverviewCard from "@/components/podcastadmin_dashboard_new/components/OverviewCard.vue";
import OvertimeCard from "@/components/podcastadmin_dashboard_new/components/OvertimeCard.vue";
import TopCountriesCard from "@/components/podcastadmin_dashboard_new/components/TopCountriesCard.vue";
import ByDeviceCard from "@/components/podcastadmin_dashboard_new/components/ByDeviceCard.vue";
import ShowsTable from "@/components/podcaster_dashboard_new/components/ShowsTable.vue";
import DateMenu from "@/components/podcastadmin_dashboard_new/components/DateMenu.vue";
import PreJamxDownloadsPopup from "@/components/PreJamxDownloadsPopup.vue";
import { useToast } from "primevue/usetoast";

const store = useStore();
const toast = useToast();

const overviewConfig = computed(() => [
  {
    icon: "pi-eye",
    title: "Views",
    key: "total_views",
    type: "episode_imp",
    clickable: true,
    status: true,
  },
  {
    icon: "pi-headphones",
    title: "Unique Listeners",
    key: "total_unique_listeners",
    type: "",
    clickable: false,
  },
  {
    icon: "pi-percentage",
    title: "CTR",
    key: "overall_ctr",
    type: "",
    clickable: false,
    isPercent: true,
  },
  {
    icon: "pi-bullseye",
    title: "Impressions",
    key: "impressions",
    type: "impressions",
    clickable: true,
  },
  {
    icon: "pi-file-excel",
    title: "ECPM",
    key: "ecpm",
    type: "",
    clickable: false,
    isFixed: 2,
  },
  {
    icon: "pi-dollar",
    title: "Estimated Revenue",
    key: "revenue",
    type: "revenue",
    // clickable is false if any episode is selected
    clickable: selectedEpisode.value === "all",
    isCurrency: true,
    tooltip:
      "The revenue figures mentioned are estimated and may be subject to adjustment based on actual performance.",
  },
]);

const selectedShow = ref("all");
const selectedEpisode = ref("all");
const campaignType = ref(null);
const dates = ref([new Date(moment().subtract(1, "week")), new Date(moment())]);
const loadingOverviews = ref(false);
const loadingOvertimeData = ref(false);
const showOverviewPlaceholder = ref(false);
const podcasterId = store.getters.podcaster.podcaster_id;
const isProOrPlus = ["jamx pro plus", "jamx pro"].includes(
  store.getters.podcaster.plan
);
const eventType = ref("episode_imp");
const overtimeEventType = ref("episode_imp");
const overviews = ref(overviewConfig.value);
const token = localStorage.getItem("podcasterToken");
const campaignTypeOptions = [
  { label: "All", value: null },
  { label: "Publisher", value: "publisher" },
  { label: "Promotion", value: "promotion" },
];
const overtimeData = ref([]);
const loadingTopByCountries = ref(false);
const topByCountries = ref([]);
const loadingTopByDevices = ref(false);
const topByDevices = ref([]);
const loadingShowsData = ref(false);
const showsData = ref([]);
const preJamxShows = ref([]);
const preJamxModalVisible = ref(false);
const loadingPreJamx = ref(false);
const savingPreJamx = ref(false);
const PREJAMX_DISMISS_KEY = `prejamx.dismiss.${podcasterId}`;
const PREJAMX_REMIND_KEY = `prejamx.remind.${podcasterId}`;

const connectedPublisherId = store.getters.podcaster.connected_publisher_id;

const type = computed(() => {
  if (eventType.value === "episode_imp") {
    return "Reach";
  } else if (eventType.value === "episode_play") {
    return "Sessions";
  }
  return "Reach";
});

const overtimeType = computed(() => {
  const keyMap = {
    episode_imp: "Reach",
    episode_play: "Sessions",
    impressions: "Impressions",
    revenue: "Revenue",
  };
  return keyMap[overtimeEventType.value] || "Reach";
});

const setDateRange = (dateRange) => {
  dates.value = dateRange;
};

const handleActiveOverviewCard = (type, status, clickable) => {
  if (!clickable) return; // If the card is not clickable, do nothing
  overtimeEventType.value = type;
  if (status && eventType.value !== type) {
    eventType.value = type;
    fetchTopCountries();
    fetchTopDevices();
  }
};

const selectShow = (show) => {
  console.log("Selected show: ", show);
  selectedShow.value = show;
  selectedEpisode.value = "all";
  fetchOverviews();
  fetchOvertime();
  fetchTopCountries();
  fetchTopDevices();
  fetchShowsData();
};

const selectEpisode = (episode) => {
  console.log("Selected episode: ", episode);
  selectedEpisode.value = episode;
  overtimeEventType.value = eventType.value;

  fetchOverviews();
  fetchOvertime();
  fetchTopCountries();
  fetchTopDevices();
  fetchShowsData();
};

const generateDiff = (current, past) => {
  const currNum = parseFloat(current);
  const pastNum = parseFloat(past);
  if (isNaN(currNum) || isNaN(pastNum) || pastNum === 0) return "N/A"; // Avoid division by zero or invalid numbers
  const diff = ((currNum - pastNum) / pastNum) * 100;
  const sign = diff > 0 ? "+" : "";
  return `${sign}${diff.toFixed(2)}%`;
};

const formatNumber = (num) => {
  const value = Number(num);
  if (isNaN(value)) return num;
  if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
  if (value >= 1_000) return (value / 1_000).toFixed(1) + "K";
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const fetchOverviews = async () => {
  try {
    loadingOverviews.value = true;
    const { data } = await axios.get(API_BASE_URL + "/podcast_events/totals", {
      params: {
        podcasterId: podcasterId,
        showId: selectedShow.value,
        episodeId: selectedEpisode.value,
        startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
        endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
        campaignType: campaignType.value,
        isProOrPlus,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (data) {
      const hasNonZeroMetric = overviewConfig.value.some((cfg) => {
        const numericValue = Number(data[cfg.key]);
        return !Number.isNaN(numericValue) && numericValue !== 0;
      });

      overviews.value = overviewConfig.value.map((cfg) => {
        let total;
        if (cfg.isPercent) {
          total = data[cfg.key] ? `${(data[cfg.key] * 100).toFixed(1)}%` : `0%`;
        } else if (cfg.isFixed) {
          total = data[cfg.key] ? `${data[cfg.key].toFixed(cfg.isFixed)}` : `0`;
        } else if (cfg.isCurrency) {
          total = `$${formatNumber(data[cfg.key])}`;
        } else {
          total = formatNumber(data[cfg.key]);
        }
        const past = data.pastTotals?.[cfg.key];
        return {
          icon: cfg.icon,
          title: cfg.title,
          total:
            cfg.title === "Estimated Revenue" && selectedEpisode.value !== "all"
              ? "N/A"
              : total,
          type: cfg.type,
          clickable: cfg.clickable,
          status: cfg.status,
          diff: generateDiff(data[cfg.key], past) || "N/A",
          tooltip: cfg.tooltip,
        };
      });

      showOverviewPlaceholder.value = !hasNonZeroMetric;
    }
  } catch (error) {
    console.error("Error fetching shows:", error);
  } finally {
    setTimeout(() => {
      loadingOverviews.value = false;
    }, 300);
  }
};

const fetchOvertime = async () => {
  try {
    loadingOvertimeData.value = true;
    const { data } = await axios.get(
      API_BASE_URL + "/podcast_events/over-time",
      {
        params: {
          podcasterId: podcasterId,
          showId: selectedShow.value,
          episodeId: selectedEpisode.value,
          startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
          endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
          campaignType: campaignType.value,
          isProOrPlus,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (data) {
      overtimeData.value = data.over_time_data;
    }
  } catch (error) {
    console.error("Error fetching shows:", error);
  } finally {
    setTimeout(() => {
      loadingOvertimeData.value = false;
    }, 300);
  }
};

const fetchTopCountries = async () => {
  try {
    loadingTopByCountries.value = true;
    const { data } = await axios.get(
      `${API_BASE_URL}/podcast_events/top-by-countries`,
      {
        params: {
          podcasterId: podcasterId,
          showId: selectedShow.value,
          episodeId: selectedEpisode.value,
          startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
          endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
          eventTypes: eventType.value,
          campaignType: campaignType.value,
          isProOrPlus,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (data) {
      topByCountries.value = data.top_by_countries;
    }
  } catch (err) {
    console.log(err.message);
  } finally {
    setTimeout(() => {
      loadingTopByCountries.value = false;
    }, 300);
  }
};

const fetchTopDevices = async () => {
  try {
    loadingTopByDevices.value = true;
    const { data } = await axios.get(
      `${API_BASE_URL}/podcast_events/by-device-type`,
      {
        params: {
          podcasterId: podcasterId,
          showId: selectedShow.value,
          episodeId: selectedEpisode.value,
          startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
          endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
          eventTypes: eventType.value,
          campaignType: campaignType.value,
          isProOrPlus,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (data) {
      topByDevices.value = data.by_device_type;
    }
  } catch (err) {
    console.log(err.message);
  } finally {
    setTimeout(() => {
      loadingTopByDevices.value = false;
    }, 300);
  }
};

const fetchShowsData = async () => {
  try {
    loadingShowsData.value = true;
    if (selectedShow.value === "all") {
      const { data } = await axios.get(
        `${API_BASE_URL}/podcast_events/shows-data`,
        {
          params: {
            podcasterId: podcasterId,
            startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
            endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
            campaignType: campaignType.value,
            isProOrPlus,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data) {
        showsData.value = data.shows_data;
      }
    } else {
      const { data } = await axios.get(
        `${API_BASE_URL}/podcast_events/episode-data`,
        {
          params: {
            podcasterId: podcasterId,
            showId: selectedShow.value,
            startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
            endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
            campaignType: campaignType.value,
            isProOrPlus,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data) {
        showsData.value = data.episode_data;
      }
    }
  } catch (err) {
    console.log(err.message);
  } finally {
    setTimeout(() => {
      loadingShowsData.value = false;
    }, 300);
  }
};

const shouldSkipPreJamxPrompt = () => {
  return (
    localStorage.getItem(PREJAMX_DISMISS_KEY) === "true" ||
    sessionStorage.getItem(PREJAMX_REMIND_KEY) === "true"
  );
};

const fetchPreJamxDownloads = async () => {
  try {
    if (shouldSkipPreJamxPrompt()) {
      return;
    }

    loadingPreJamx.value = true;
    const { data } = await axios.get(
      `${API_BASE_URL}/podcaster/pre-jamx-downloads`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const payload = data?.data || {};
    preJamxShows.value = payload.shows || [];
    preJamxModalVisible.value =
      preJamxShows.value.length > 0 && !shouldSkipPreJamxPrompt();
  } catch (error) {
    console.error("Error fetching pre-Jamx downloads:", error);
  } finally {
    loadingPreJamx.value = false;
  }
};

const handlePreJamxSave = async (updates) => {
  if (!Array.isArray(updates) || !updates.length) {
    toast.add({
      severity: "warn",
      summary: "Nothing to save",
      detail: "Add downloads for at least one show to continue.",
      life: 4000,
    });
    return;
  }

  try {
    savingPreJamx.value = true;
    const { data } = await axios.post(
      `${API_BASE_URL}/podcaster/pre-jamx-downloads`,
      { shows: updates },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const payload = data?.data || {};
    preJamxShows.value = payload.shows || [];
    const hasRemainingMissing = preJamxShows.value.length > 0;

    if (!hasRemainingMissing) {
      preJamxModalVisible.value = false;
      sessionStorage.removeItem(PREJAMX_REMIND_KEY);
      toast.add({
        severity: "success",
        summary: "Saved",
        detail: "Thanks for sharing your pre-JamX downloads.",
        life: 4000,
      });
    } else {
      toast.add({
        severity: "info",
        summary: "Saved",
        detail: "Some shows still need their averages.",
        life: 4000,
      });
    }

    store.dispatch("loadShows", { token });
  } catch (error) {
    console.error("Error saving pre-Jamx downloads:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail:
        error?.response?.data?.message ||
        "Failed to save your average downloads",
      life: 4000,
    });
  } finally {
    savingPreJamx.value = false;
  }
};

const handlePreJamxRemindLater = () => {
  sessionStorage.setItem(PREJAMX_REMIND_KEY, "true");
  preJamxModalVisible.value = false;
};

const handlePreJamxDismiss = (payload = {}) => {
  if (payload.dontShowAgain) {
    localStorage.setItem(PREJAMX_DISMISS_KEY, "true");
  }
  preJamxModalVisible.value = false;
};

watch([dates, campaignType], () => {
  if (dates.value && dates.value[0] !== null && dates.value[1] !== null) {
    fetchOverviews();
    fetchOvertime();
    fetchTopCountries();
    fetchTopDevices();
    fetchShowsData();
  }
});

onMounted(() => {
  fetchPreJamxDownloads();
});
</script>
